import { useEffect, useState } from "react";
import { dds } from "./ddsClient";
import { withAuthContext } from "./withAuthContext";

export function useDDSGetData(preset, options = {}) {
  const { enabled = true } = options;

  // Identity of the query currently being asked. null = nothing to ask.
  const presetKey = enabled && preset ? JSON.stringify(preset) : null;

  // Rows are stored TOGETHER with the preset they answer, and matched during
  // render. This hook used to keep `data` in its own state and simply
  // early-return when the query stopped applying, which left the previous
  // query's rows readable alongside `loading === false` — consumers took that
  // pair as "settled and current" and acted on it.
  //
  // Clearing inside the effect instead is not enough either: sibling effects in
  // the same commit run BEFORE that clear lands, so TXLivePage's auto-select
  // still saw the old rows for one render — which is all the revert loop
  // needed. Deriving the answer here closes the window: the moment the preset
  // changes, `data` reads as null in the very same render that asks for the new
  // one.
  const [result, setResult] = useState({ key: null, data: null, error: null });

  useEffect(() => {
    if (!presetKey) return;

    let active = true;

    // Wrapped at fetch time, not in the dependency list: the token is read
    // fresh on every run so a refresh mid-session is picked up, and the effect
    // still re-runs only when the caller's own preset changes.
    //
    // The `setLoading(true)` that sat here is gone deliberately: dev replaced
    // the separate data/loading/error state with one `result` object and a
    // derived `loading` (see below), so there is no setter to call any more.
    dds.getStaticData(withAuthContext(preset))
      .then(res => {
        if (active) setResult({ key: presetKey, data: res, error: null });
      })
      .catch(err => {
        if (active) setResult({ key: presetKey, data: null, error: err });
      });

    return () => {
      active = false;
    };
    // `preset` is deliberately not a dep — presetKey is its serialisation, and
    // depending on the object itself would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const settled = presetKey !== null && result.key === presetKey;

  return {
    data: settled ? result.data : null,
    loading: presetKey !== null && !settled,
    error: settled ? result.error : null,
  };
}
